import { useEffect, useState } from 'react';

import { Input, Message, Pagination, Spin } from '@arco-design/web-react';

import { EmptyTips, dateFormatRender } from '@/components';
import { formatBytes } from '@/utils';
import { cs } from '@/utils/className';

import { getModelScopeModels } from './service';
import styles from './style.module.less';

const DEFAULT_PAGE_SIZE = 12;

/** 从 ModelScope 模型数据中提取厂商名称 */
export function getModelScopeVendor(model?: { Organization?: { FullName?: string; Name?: string } }) {
  return model?.Organization?.FullName || model?.Organization?.Name;
}

interface ModelSelectorProps {
  disabled?: boolean;
  value?: string;
  onChange?: (value: string, model: any) => void;
}

export function ModelSelector({ disabled = false, value, onChange }: ModelSelectorProps) {
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [modelList, setModelList] = useState<any[]>([]);
  const [internalValue, setInternalValue] = useState<string | null>(value);

  const fetchModels = async (page: number, name?: string) => {
    setLoading(true);
    try {
      const response = await getModelScopeModels({
        PageNumber: page,
        PageSize: pagination.pageSize,
        Name: name,
      });

      if (response?.Code === 200) {
        const { Models, TotalCount }: any = response?.Data?.Model || {};
        setModelList(Models || []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          total: TotalCount || 0,
        }));
      } else {
        setModelList([]);
        setPagination((prev) => ({ ...prev, current: page, total: 0 }));
      }
    } catch (error) {
      Message.error('获取模型列表失败');
      setModelList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels(1);
  }, []);

  const handleSearch = (value?: string) => {
    setKeyword(value);
    fetchModels(1, value);
  };

  const handlePageChange = (page: number) => {
    fetchModels(page, keyword);
  };

  const handleCardClick = (model: any) => {
    if (disabled) return;

    const { Path, Name } = model;
    const val = `${Path}/${Name}`;
    setInternalValue(val);
    onChange?.(val, model);
  };

  return (
    <div className={styles['container']}>
      <div className={styles['search-bar']}>
        <h5 className={styles.title}>Modelscope 模型列表</h5>
        <Input.Search
          placeholder="搜索模型名称"
          allowClear
          onSearch={handleSearch}
          value={keyword}
          onChange={setKeyword}
          disabled={disabled}
          style={{ width: '40%' }}
          onClear={() => {
            handleSearch();
          }}
        />
      </div>

      <Spin loading={loading} block>
        <div className={styles['model-grid']}>
          {modelList.map((model) => {
            const size = model.StorageSize ? formatBytes(model.StorageSize) : null;
            const title = [dateFormatRender(model.LastUpdatedTime), size, model.License].filter(Boolean).join(' | ');
            return (
              <div
                key={model.Id}
                className={`${styles['model-card']} ${internalValue === `${model.Path}/${model.Name}` ? styles['selected'] : ''}`}
                onClick={() => handleCardClick(model)}
              >
                {/* Line 1: Path/Name */}
                <div className={styles['card-line-1']} title={`${model.Path} / ${model.Name}`}>
                  {model.Path} / {model.Name}
                </div>

                <div className={styles['card-line-2']}>
                  {model?.Organization?.Avatar ? (
                    <img src={model?.Organization?.Avatar} alt="" className={styles['avatar']} />
                  ) : (
                    <span className={styles['avatar']}> </span>
                  )}
                  <span className={styles['meta-item']}>{getModelScopeVendor(model)}</span>
                  <span className={styles['separator']}> | </span>
                  <span className={cs('ellipsis', styles['meta-item'])} title={title}>
                    <span>{dateFormatRender(model.LastUpdatedTime * 1000)}</span>
                    {size && (
                      <>
                        <span className={styles['separator']}> | </span>
                        <span>{size}</span>
                      </>
                    )}
                    {model.License && (
                      <>
                        <span className={styles['separator']}> | </span>
                        <span style={{ textTransform: 'capitalize' }}>{model.License}</span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {modelList.length === 0 && !loading && (
          <div className={styles['empty-wrapper']}>
            <EmptyTips tips="暂无模型数据" />
          </div>
        )}

        {/* Pagination */}
        {modelList.length > 0 && (
          <div className={styles['pagination-wrapper']}>
            <Pagination
              size="mini"
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showTotal
              disabled={disabled}
            />
          </div>
        )}
      </Spin>
    </div>
  );
}
